import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Form, Row, Col } from "react-bootstrap";
import validate from "validate.js";
import {
  createOrUpdateConfiguration,
  configurationRedirectToList,
} from "pages/AdminManage/actions/ConfigurationActions";

// --- Component Configuration ---
const configType = "source"; // <--- This is the only change needed
const capitalizedType = "Source"; // <--- For display text

// --- Validation Schema ---
const schema = {
  value: {
    presence: {
      allowEmpty: false,
      message: `^${capitalizedType} value is required`,
    },
  },
  sort_order: {
    presence: { allowEmpty: false, message: "^Sort Order is required" },
    numericality: {
      onlyInteger: true,
      message: "^Sort Order must be a whole number",
    },
  },
};

const SourceAddUpdateModal = ({
  show,
  handleClose,
  isEdit = false,
  data = null, // The record to edit
  object_viewed_id,
}) => {
  const dispatch = useDispatch();

  const { validation_error, redirectToList, loading } = useSelector(
    (s) => s.configurationState
  );

  const [formState, setFormState] = useState({
    isValid: false,
    values: {
      value: "",
      sort_order: "", // Initialize with an empty string
    },
    touched: {},
    errors: {},
  });

  // Pre-populate form when editing
  useEffect(() => {
    if (isEdit && data) {
      const values = {
        id: data.id,
        value: data.value || "",
        // Ensure sort_order is a string for the form
        sort_order: data.sort_order !== null ? String(data.sort_order) : "",
      };
      setFormState((fs) => ({
        ...fs,
        values,
      }));
    }
  }, [data, isEdit]);

  // Watch for backend validation errors from Redux
  useEffect(() => {
    if (validation_error) {
      setFormState((fs) => ({
        ...fs,
        isValid: false,
        errors: validation_error || {},
      }));
    }
  }, [validation_error]);

  // Perform local validation whenever form values change
  useEffect(() => {
    const errors = validate(formState.values, schema);
    setFormState((fs) => ({
      ...fs,
      isValid: !errors,
      errors: errors || {},
    }));
  }, [formState.values]);

  // Close the modal on successful submission
  useEffect(() => {
    if (redirectToList) {
      onModelClose();
    }
  }, [redirectToList]);

  // --- Event Handlers ---

  const handleChange = (e) => {
    e.persist();
    let { name, value } = e.target;

    // For sort_order, only allow digits
    if (name === "sort_order") {
      value = value.replace(/\D/g, "");
    }

    setFormState((fs) => ({
      ...fs,
      values: { ...fs.values, [name]: value },
      touched: { ...fs.touched, [name]: true },
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formState.isValid) {
      const method = formState.values.id ? "PUT" : "POST";
      // Prepare payload for API, converting sort_order back to a number
      const payload = {
        ...formState.values,
        type: configType,
        sort_order: parseInt(formState.values.sort_order, 10),
      };

      dispatch(
        createOrUpdateConfiguration(
          configType,
          payload,
          method,
          object_viewed_id
        )
      );
    } else {
      // Mark all fields as touched to show errors on first submit attempt
      const touched = Object.keys(formState.values).reduce(
        (acc, key) => ({ ...acc, [key]: true }),
        {}
      );
      setFormState((fs) => ({ ...fs, touched }));
    }
  };

  const hasError = (field) =>
    formState.touched[field] && formState.errors[field];

  const onModelClose = () => {
    // Reset form state completely
    setFormState({
      isValid: false,
      values: { value: "", sort_order: "" },
      touched: {},
      errors: {},
    });
    // Dispatch an action to clear the redirect flag and any old data
    dispatch(configurationRedirectToList());
    handleClose();
  };

  return (
    <Modal show={show} onHide={onModelClose} centered>
      <div className="p-2">
        <Modal.Header className="p-2" closeButton>
          <Modal.Title>
            <h5 className="font-20 font-weight-bold heading-color">
              {isEdit
                ? `Update ${capitalizedType}`
                : `Add New ${capitalizedType}`}
            </h5>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col lg={12}>
                <Form.Group className="mb-3">
                  <Form.Label>{capitalizedType} Value</Form.Label>
                  <Form.Control
                    type="text"
                    name="value"
                    placeholder={`Enter ${capitalizedType.toLowerCase()} value`}
                    className={`input-field ${
                      hasError("value") ? "is-invalid" : ""
                    }`}
                    onChange={handleChange}
                    value={formState.values.value}
                  />
                  {hasError("value") && (
                    <Form.Control.Feedback type="invalid">
                      {formState.errors.value[0]}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>

              <Col lg={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Sort Order</Form.Label>
                  <Form.Control
                    type="text" // Use text type to allow empty string
                    name="sort_order"
                    placeholder="Enter sort order"
                    className={`input-field ${
                      hasError("sort_order") ? "is-invalid" : ""
                    }`}
                    onChange={handleChange}
                    value={formState.values.sort_order}
                  />
                  {hasError("sort_order") && (
                    <Form.Control.Feedback type="invalid">
                      {formState.errors.sort_order[0]}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col lg={6}>
                <button
                  type="button"
                  className="c-b-btn w-100"
                  onClick={onModelClose}
                >
                  Cancel
                </button>
              </Col>
              <Col lg={6}>
                <button
                  type="submit"
                  className="n-u-btn w-100"
                  disabled={!formState.isValid || loading}
                >
                  {loading ? "Processing..." : isEdit ? "Update" : "Add"}
                </button>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
      </div>
    </Modal>
  );
};

export default SourceAddUpdateModal;
